import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import { statsApi } from '../../utils/api';

interface DashboardStats {
  users: number;
  posts: number;
  replies: number;
  categories: number;
}

const AdminDashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { language } = useLanguageStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await statsApi.getDashboardStats();
        if (response.success && response.data) {
          setStats(response.data);
        }
      } catch (error) {
        console.error('获取统计数据失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [language]);

  const isMaster = user?.role === '站长';

  if (!isAuthenticated || !['管理员', '站长', '超级管理员', '吧主', '吧务'].includes(user?.role || '')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{'管理后台'}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">{'用户总数'}</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">{loading ? '--' : stats?.users || 0}</p>
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">{'帖子总数'}</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">{loading ? '--' : stats?.posts || 0}</p>
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">{'回复总数'}</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">{loading ? '--' : stats?.replies || 0}</p>
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">{'版块总数'}</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">{loading ? '--' : stats?.categories || 0}</p>
        </div>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{'快捷操作'}</h2>
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${isMaster ? '5' : '4'} gap-4`}>
          <Link
            to="/admin/users"
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <h3 className="font-medium text-gray-900">{'用户管理'}</h3>
            <p className="text-sm text-gray-500 mt-1">{'管理用户账号'}</p>
          </Link>
          <Link
            to="/admin/posts"
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <h3 className="font-medium text-gray-900">{'帖子管理'}</h3>
            <p className="text-sm text-gray-500 mt-1">{'管理帖子内容'}</p>
          </Link>
          <Link
            to="/admin/categories"
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <h3 className="font-medium text-gray-900">{'版块管理'}</h3>
            <p className="text-sm text-gray-500 mt-1">{'管理论坛版块'}</p>
          </Link>
          <Link
            to="/admin/sensitive-words"
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <h3 className="font-medium text-gray-900">{'敏感词管理'}</h3>
            <p className="text-sm text-gray-500 mt-1">{'管理敏感词列表'}</p>
          </Link>
          {isMaster && (
            <Link
              to="/admin/site-config"
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <h3 className="font-medium text-gray-900">{'站点配置'}</h3>
              <p className="text-sm text-gray-500 mt-1">{'配置站点设置'}</p>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
